import { createUseStyles } from 'react-jss';
import { Classes } from 'jss';
import { ActionPalette, BrandPalette, GrayscalePalette } from '@tcl-boron-colors/colors';
import { StatusVariant, PRIMARY_STATUS_COLORS, SECONDARY_STATUS_COLORS } from '@tcl-argon-utils/utils';

const getValueContainerBorderColor: (status: StatusVariant, isPrimary: boolean) => string = (status, isPrimary) => {
  const isDefault = status === 'default';
  if (isPrimary) {
    return isDefault ? 'transparent' : PRIMARY_STATUS_COLORS[status];
  }
  return PRIMARY_STATUS_COLORS[status];
};

const labelBaseStyles = {
  cursor: 'pointer',
  padding: '7px 0px 7px 6px',
  transition: 'background-color 150ms ease-out, color 150ms ease-out',
  '&:hover': {
    backgroundColor: ActionPalette[200],
  },
  height: 'max-content',
  color: ActionPalette[600],
  fontSize: '14px',
  margin: '0px !important',
};
export interface DropdownStylesData {
  isDisabled: boolean;
  isPrimary: boolean;
  status: StatusVariant;
  isEmpty: boolean;
  isLoading: boolean;
}
export const useMultiSelectDropdownStyles: (
  data: DropdownStylesData & { theme?: Jss.Theme },
) => Classes<
  | 'clearButton'
  | 'disabled'
  | 'highlightedMenuItem'
  | 'inputWrapper'
  | 'invisibleMenu'
  | 'itemRow'
  | 'menu'
  | 'placeholder'
  | 'value'
  | 'valueContainer'
  | 'checkboxLabel'
  | 'selectedCheckboxLabel'
  | 'selectedValuesText'
  | 'valueWrapper'
  | 'singleMenuItem'
  | 'searchBox'
  | 'searchBoxPositionClosed'
  | 'selectedItemsDivider'
  | 'selectedMenuItem'
  | 'valueContainerComboBox'
  | 'valueContainerDropdown'
  | 'buttonWrapper'
  | 'menuLoaderAndNoOp'
  | 'clearSelectionsButton'
  | 'buttonDivider'
  | 'clearSelectionsButtonHover'
  | 'clearSelectionWrapper'
  | 'selectedText'
  | 'noneClearWrapper'
  | 'multiClearWrapper'
  | 'noResultsFoundMenu'
  | 'menuItemCheckboxLabel'
  | 'menuItemCheckboxContainer'
  | 'scrollContainer'
  | 'loaderMenu'
> = createUseStyles({
  itemRow: {
    display: 'flex',
    alignItems: 'center',
  },
  clearButton: {
    outline: 'none',
    cursor: 'inherit',
    borderStyle: 'solid',
    borderWidth: '2px',
    borderColor: 'transparent',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    height: '22px',
    width: '22px',
    padding: '0',
    '&:hover': {
      borderStyle: 'solid',
      borderColor: ActionPalette[200],
      backgroundColor: ActionPalette[200],
      borderWidth: '2px',
    },
    '&:focus-visible': {
      borderStyle: 'solid',
      borderColor: ActionPalette[300],
      backgroundColor: 'transparent',
      borderWidth: '3px',
    },
  },
  buttonDivider: {
    background: 'none',
    border: 'none',
    borderRight: `1px solid ${ActionPalette[400]}`,
    height: '21px',
    margin: '0px 8px 0px 0px',
    padding: '0px 5px 0px 0px',
    boxSizing: 'border-box',
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    width: '22px',
    height: '22px',
    boxSizing: 'border-box',
    justifyContent: 'center',
  },
  disabled: {
    opacity: '0.5',
    pointerEvents: 'none',
  },
  highlightedMenuItem: {
    backgroundColor: ActionPalette[200],
  },
  inputWrapper: {
    position: 'relative',
  },
  comboBoxInput: {
    padding: '0px',
    fontWeight: 400,
  },
  invisibleMenu: {
    visibility: 'hidden',
    pointerEvents: 'none',
  },
  menu: {
    backgroundColor: 'white',
    border: '1px solid',
    borderColor: ActionPalette[400],
    borderRadius: '2px',
    listStyle: 'none',
    margin: 0,
    maxHeight: '516px',
    outline: 0,
    overflow: 'hidden',
    zIndex: 1,
    padding: '0px',
  },
  scrollContainer: {
    overflowY: 'auto',
    maxHeight: '500px',
    padding: '8px 0px',
  },
  noResultsFoundMenu: {
    height: '38px',
  },
  loaderMenu: {
    height: '40px',
  },
  checkboxLabel: { ...labelBaseStyles, flex: '0 1 auto' },
  selectedCheckboxLabel: {
    ...labelBaseStyles,
    background: 'transparent',
  },
  selectedMenuItem: {
    backgroundColor: ActionPalette[400],
    fontWeight: '700',
  },
  selectedItemsDivider: {
    backgroundColor: GrayscalePalette[400],
    paddingLeft: '4px',
    height: '9px',
    border: `4px solid ${GrayscalePalette[100]}`,
    borderLeftWidth: '8px',
    borderRightWidth: '8px',
    boxSizing: 'border-box',
    //need to have !important in order to override some docs styling
    marginTop: '3px !important',
    marginBottom: '3px',
  },
  placeholder: {
    color: ({ isDisabled }: DropdownStylesData) => (isDisabled ? ActionPalette[400] : ActionPalette[500]),
  },
  value: {
    fontSize: ({ isEmpty }: DropdownStylesData) => (!isEmpty ? '12px' : '14px'),
    flex: '1 1 auto',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontWeight: 400,
  },
  valueContainer: {
    '&:focus, &[aria-expanded="true"]': {
      backgroundColor: '',
      borderColor: ActionPalette[500],
    },
    '&:hover': {
      borderColor: ActionPalette[500],
    },
    transition: 'border-color 150ms ease-out',
    alignItems: 'center',
    backgroundColor: ({ status }: DropdownStylesData) => SECONDARY_STATUS_COLORS[status],
    border: '1px solid',
    borderColor: ({ status, isPrimary }: DropdownStylesData) => getValueContainerBorderColor(status, isPrimary),
    borderRadius: ({ isPrimary }: DropdownStylesData) => (isPrimary ? '20px' : '3px'),
    boxSizing: 'border-box',
    boxShadow: ({ isPrimary }: DropdownStylesData) => (isPrimary ? `0px 1px 3px 1px ${GrayscalePalette[300]}` : ''),
    cursor: 'pointer',
    display: 'flex',
    height: '40px',
    justifyContent: 'space-between',
    // This is OK because we outline the valueContainer with a colored border
    outline: 0,
    padding: '12px 10px 12px 20px',
    color: ActionPalette[600],
  },
  valueContainerComboBox: {
    '&:focus-within, &[aria-expanded="true"], &:active': {
      backgroundColor: ({ status }: DropdownStylesData) =>
        status === 'default' ? GrayscalePalette[200] : SECONDARY_STATUS_COLORS[status],
      borderColor: ActionPalette[500],
    },
  },
  valueContainerDropdown: {
    '&:focus, &[aria-expanded="true"]': {
      backgroundColor: '',
      borderColor: ActionPalette[500],
    },
  },
  valueWrapper: {
    flexBasis: '0px',
    flexGrow: 1,
    display: 'flex',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    paddingRight: '12px',
  },
  selectedValuesText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  searchBox: {
    outline: 'none',
    border: 'none',
    top: '11px',
    fontSize: '14px',
    background: 'transparent',
    width: '100%',
    '&::placeholder': {
      color: ({ isDisabled }: DropdownStylesData) => (isDisabled ? ActionPalette[400] : ActionPalette[500]),
      fontSize: '12px',
      flex: '1 1 auto',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontWeight: 400,
    },
  },
  searchBoxPositionClosed: {
    position: 'absolute',
    opacity: ({ isEmpty }: DropdownStylesData) => (!isEmpty ? '100' : '0'),
  },
  singleMenuItem: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 0px',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: '16px',
    minHeight: '34px',
    padding: '0px 15px',
    color: ActionPalette[600],
    transition: 'background-color 150ms ease-out, color 150ms ease-out',
    userSelect: 'none',
  },
  menuLoaderAndNoOp: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    lineHeight: '12px',
    padding: ({ isLoading }: DropdownStylesData) => (isLoading ? '8px 0px' : '13px 0px'),
    color: GrayscalePalette[500],
    justifyContent: 'center',
    textAlign: 'center',
  },
  clearSelectionWrapper: {
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 150ms ease-out, color 150ms ease-out',
    userSelect: 'none',
  },
  noneClearWrapper: {
    cursor: 'pointer',
    height: '34px',
    padding: '0px 17px',
    fontSize: '14px',
  },
  multiClearWrapper: {
    padding: '4px 0px',
  },
  selectedText: {
    fontSize: '10px',
    fontWeight: '500',
    letterSpacing: '2px',
    lineHeight: '12px',
    textTransform: 'uppercase',
    margin: '0 !important',
  },
  selectedTextHierarchical: {
    paddingLeft: '12px',
  },
  clearSelectionsButton: {
    outline: 'none',
    border: 'none',
    marginLeft: 'auto',
    marightRight: '10px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: ActionPalette[300],
    fontWeight: '500',
    textDecoration: 'none',
    fontSize: '12px',
    lineHeight: '12px',
    paddingRight: '12px',
  },
  clearSelectionsButtonHover: {
    textDecoration: 'underline',
  },
  menuItemCheckboxContainer: {
    position: 'relative',
    display: 'flex',
    minHeight: '34px',
    // Necessary for IE11.
    // Without this, checkbox is vertically centered
    // next to option label.
    alignItems: 'flex-start',
  },
  menuItemCheckboxLabel: {
    position: 'relative',
    cursor: 'pointer',
    // Necessary for IE11.
    // Without this, if the option has text that wraps,
    // then the checkbox will be flattened.
    width: '100%',
  },
  boldedLabel: {
    // Vertical gap between lines and borders of dropdown option.
    padding: '7px 0',
    fontSize: '14px',
    display: 'flex',
    // Wrap text.
    flexWrap: 'wrap',
    // Preserve space characters at ends of strings of BoldedLabel component.
    whiteSpace: 'pre-wrap',
    position: 'relative',
    top: '-1px',
    lineHeight: '20px',
    rowGap: '4px',
  },
  menuStyles: {
    overflowY: 'auto',
    padding: 0,
    listStyle: 'none',
    position: 'relative',
    backgroundColor: 'white',
    border: '1px solid',
    borderColor: ActionPalette[400],
    borderRadius: '2px',
    margin: 0,
    maxHeight: '516px',
    outline: 0,
    overflow: 'hidden',
    zIndex: 1,
    '&::-webkit-scrollbar-thumb': {
      height: '100px',
    },
  },
  relativeMenuContainer: {
    position: 'relative',
    //need to have !important in order to override some docs styling
    marginTop: '8px !important',
    marginBottom: '8px',
  },
  relativeMenuContainerHierarchical: {
    position: 'relative',
    //need to have !important in order to override some docs styling
    marginTop: '12px !important',
    marginBottom: '12px',
  },
});

export const useCheckboxStyles: () => Classes<'svg' | 'checkboxContainer' | 'hoverCheck'> = createUseStyles({
  checkboxContainer: {
    height: '16px',
    width: '16px',
    border: '1px solid',
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    marginRight: '10px',
    transition: 'borderColor 150ms ease-out, backgroundColor 150ms ease-out',
    boxSizing: 'border-box',
  },
  checkboxContainerDisabledSelected: {
    backgroundColor: GrayscalePalette[400],
    borderColor: GrayscalePalette[400],
  },
  checkboxContainerSelected: {
    backgroundColor: ActionPalette[600],
    borderColor: ActionPalette[600],
  },
  checkboxContainerDisabledUnselected: {
    backgroundColor: GrayscalePalette[100],
    borderColor: GrayscalePalette[400],
  },
  checkboxContainerUnselected: {
    backgroundColor: GrayscalePalette[100],
    borderColor: GrayscalePalette[500],
  },
  svg: {
    height: '12px',
    width: '12px',
    display: 'block',
    margin: 'auto !important',
    '& path': {
      stroke: 'white',
      strokeWidth: '.3',
      fill: '',
      strokeDasharray: 48,
      strokeDashoffset: 48,
      animation: 'stroke .3s ease-out 150ms forwards',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) ': {
        fill: 'white',
      },
    },
    '& rect': {
      stroke: 'white',
      strokeWidth: '.3',
      fill: '',
      strokeDasharray: 48,
      strokeDashoffset: 48,
      animation: 'stroke .3s ease-out 150ms forwards',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) ': {
        fill: 'white',
      },
    },
  },
  svgDisabled: {
    '& path': {
      fill: 'white',
      animation: '',
    },
    '& rect': {
      fill: 'white',
      animation: '',
    },
  },
  hoverCheck: {
    height: '12px',
    width: '12px',
    display: 'block',
    margin: 'auto !important',
    '& path': {
      fill: `${BrandPalette[700]}`,
    },
  },
  selectCheckboxLabel: {
    width: '100%',
    display: 'flex',
    margin: '0 !important',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '400',
    paddingLeft: '12px',
    lineHeight: '20px',
    alignItems: 'center',
  },
  selectNoneCheckboxLabel: {
    paddingTop: '7px',
    paddingBottom: '7px',
  },
  selectCheckboxHierarchicalLabel: {
    paddingTop: '6px',
    paddingBottom: '2px',
    alignItems: 'flex-start',
  },
  treeStructureSelectedCheckboxLabel: {
    minHeight: '16px',
    paddingTop: '9px',
    paddingBottom: '9px',
  },
  singleSelectedItemLabel: {
    backgroundColor: ActionPalette[400],
    fontWeight: '700',
  },
  input: {
    appearance: 'none',
    margin: '0',
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) ': {
      opacity: '0',
      width: '0px',
      height: '0px',
    },
  },
  multiSelectCheckboxWrapper: {
    width: '26px',
  },
  dividerLabel: {
    paddingTop: '5px',
  },
});
